/**
 * TenantTableRow Component
 *
 * Renders a single row in the tenants table.
 *
 * @module features/tenants/components/TenantTable/TenantTableRow
 */

import React from 'react';
import {
  EditIcon,
  TrashIcon,
  RefreshIcon,
  CheckCircleIcon,
  AlertIcon,
  UndoIcon,
  ArchiveIcon,
  EyeIcon,
  ArrowUpIcon,
  HistoryIcon,
  LayersIcon,
  BuildingIcon,
} from '../../../../components/common/Icons';
import {
  getStatusBadge,
  getIsolationBadge,
  getHealthStatus,
} from '../../utils';
import type { Tenant, ContainerStatus, ContainerTransition, ProvisioningStatus, ContainerProvisioningProgress } from '../../types';
import type { AvailableUpgrade } from '../../api/upgradeApi';

interface TenantTableRowProps {
  tenant: Tenant;
  containerStatuses: Record<string, ContainerStatus>;
  containerTransitioning: Record<string, ContainerTransition>;
  provisioningStatus: Record<string, ProvisioningStatus>;
  containerProvisioningProgress: Record<string, ContainerProvisioningProgress>;
  isPurging: string | null;
  upgradeInfo?: Record<string, AvailableUpgrade>;
  onViewDetails: (tenant: Tenant) => void;
  onEdit: (tenant: Tenant) => void;
  onSuspend: (tenant: Tenant) => void;
  onRestore: (tenant: Tenant) => void;
  onPurge: (tenant: Tenant) => void;
  onArchive: (tenant: Tenant) => void;
  onHealthClick: (tenant: Tenant) => void;
  onRetryProvisioning: (tenantId: string) => void;
  onRetryContainerProvisioning?: (tenantId: string) => void;
  onResetProvisioning?: (tenantId: string) => void;
  onUpgrade?: (tenant: Tenant) => void;
  onViewUpgradeHistory?: (tenant: Tenant) => void;
  onContainerAction?: (tenantId: string, action: 'start' | 'stop' | 'restart') => void;
  onMigrate?: (tenant: Tenant) => void;
  onEnterTenant?: (tenant: Tenant) => void;
}

const renderProvisioningStatus = (
  tenant: Tenant,
  provisioningStatus: Record<string, ProvisioningStatus>,
  containerProvisioningProgress: Record<string, ContainerProvisioningProgress>,
  containerStatuses: Record<string, ContainerStatus>,
  onRetryProvisioning: (tenantId: string) => void,
  onRetryContainerProvisioning?: (tenantId: string) => void,
  onResetProvisioning?: (tenantId: string) => void,
) => {
  const status = provisioningStatus[tenant.id];
  const containerProgress = containerProvisioningProgress[tenant.id];

  // Fix 3: Detect "false completion" — tenant marked active but no containers exist
  if (
    tenant.isolation_level === 'container' &&
    tenant.status === 'active' &&
    containerStatuses[tenant.id]?.status === 'not_provisioned'
  ) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          className="badge badge-warning"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            whiteSpace: 'nowrap'
          }}
        >
          <AlertIcon size={14} />
          No Containers
        </span>
        {onResetProvisioning && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => onResetProvisioning(tenant.id)}
            title="Reset status to pending so you can re-provision"
            style={{ padding: '2px 6px', minWidth: 'auto' }}
          >
            <UndoIcon size={14} />
          </button>
        )}
      </div>
    );
  }

  // Check for active container provisioning progress first
  // (this can exist even before tenant list refresh updates tenant.status)
  if (containerProgress) {
    const cpStatus = containerProgress.status;
    const isActive = !['completed', 'failed'].includes(cpStatus);

    if (isActive) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '180px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RefreshIcon size={14} className="spin" style={{ color: 'var(--info)' }} />
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {containerProgress.message}
            </span>
          </div>
          <div style={{
            height: '6px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '3px',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${containerProgress.progress_pct}%`,
              background: 'var(--primary)',
              transition: 'width 0.3s ease',
            }} />
          </div>
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            Step {containerProgress.current_step}/{containerProgress.total_steps} — {Math.round(containerProgress.progress_pct)}%
          </span>
        </div>
      );
    }

    if (cpStatus === 'completed') {
      // Provisioning done — show the actual tenant status (e.g. "Active")
      return getStatusBadge(tenant.status);
    }

    if (cpStatus === 'failed') {
      const retryHandler = onRetryContainerProvisioning || onRetryProvisioning;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="badge badge-error"><AlertIcon size={14} /> Failed</span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => retryHandler(tenant.id)}
              title="Retry container provisioning"
            >
              <RefreshIcon size={14} />
            </button>
          </div>
          {containerProgress.message && (
            <span style={{ fontSize: '11px', color: 'var(--error)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {containerProgress.message}
            </span>
          )}
        </div>
      );
    }
  }

  // Fall through to tenant-level provisioning check
  if (tenant.status !== 'provisioning') {
    return getStatusBadge(tenant.status);
  }

  // Tenant-level (schema) provisioning status
  if (status?.status === 'in_progress') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '180px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <RefreshIcon size={14} className="spin" style={{ color: 'var(--info)' }} />
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {status.step}
          </span>
        </div>
        <div style={{
          height: '6px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '3px',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${status.progress}%`,
            background: 'var(--primary)',
            transition: 'width 0.3s ease',
          }} />
        </div>
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
          {status.progress}% complete
        </span>
      </div>
    );
  }

  if (status?.status === 'completed') {
    // Schema provisioning done — show the actual tenant status
    return getStatusBadge(tenant.status);
  }

  if (status?.status === 'failed') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span className="badge badge-error"><AlertIcon size={14} /> Failed</span>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => onRetryProvisioning(tenant.id)}
          title="Retry provisioning"
        >
          <RefreshIcon size={14} />
        </button>
      </div>
    );
  }

  // Tenant is stuck in provisioning state (no active progress)
  // Show reset button to allow recovery
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span
        className="badge badge-warning"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap'
        }}
      >
        <AlertIcon size={14} />
        Stuck
      </span>
      {onResetProvisioning && (
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => onResetProvisioning(tenant.id)}
          title="Reset stuck provisioning status"
          style={{ padding: '2px 6px', minWidth: 'auto' }}
        >
          <UndoIcon size={14} />
        </button>
      )}
    </div>
  );
};

const renderHealthCell = (
  tenant: Tenant,
  containerStatuses: Record<string, ContainerStatus>,
  containerTransitioning: Record<string, ContainerTransition>,
  onHealthClick: (tenant: Tenant) => void,
  containerProvisioningProgress?: Record<string, ContainerProvisioningProgress>,
) => {
  // During upgrading, show a pulsing upgrade badge (WQ-148)
  if (tenant.status === 'upgrading') {
    return <span className="badge badge-warning" style={{ animation: 'pulse-soft 2s ease-in-out infinite', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><RefreshIcon size={14} className="spin" /> Upgrading...</span>;
  }
  // During active provisioning, show a provisioning badge instead of container health
  const provProgress = containerProvisioningProgress?.[tenant.id];
  if (provProgress && !['completed', 'failed'].includes(provProgress.status)) {
    return <span className="badge badge-info"><RefreshIcon size={14} className="spin" /> Provisioning...</span>;
  }
  const health = getHealthStatus(tenant, containerStatuses, containerTransitioning);
  if (health.clickable) {
    return (
      <button
        className={`badge badge-${health.color}`}
        onClick={() => onHealthClick(tenant)}
        title="Click for health details"
        style={{
          border: 'none',
          cursor: 'pointer',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 0 12px rgba(124, 58, 237, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {health.label.endsWith('...') && <RefreshIcon size={14} className="spin" />}
        {health.label}
      </button>
    );
  }
  return (
    <span className={`badge badge-${health.color}`}>
      {health.label.endsWith('...') && <RefreshIcon size={14} className="spin" />}
      {health.label}
    </span>
  );
};

const renderUrlCell = (
  tenant: Tenant,
  containerStatuses: Record<string, ContainerStatus>,
  onEnterTenant?: (tenant: Tenant) => void,
) => {
  if (tenant.isolation_level === 'container') {
    const containerStatus = containerStatuses[tenant.id];
    const status = containerStatus?.status;
    const accessUrl = containerStatus?.access_url;

    if (status === 'running' && tenant.status === 'active' && onEnterTenant) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            className="tenant-url-link"
            onClick={() => onEnterTenant(tenant)}
            title={`Enter ${tenant.name} tenant context`}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--primary)',
              fontSize: '0.85em',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            Enter context
          </button>
          {accessUrl && (
            <span className="badge" style={{ fontSize: '10px', padding: '1px 4px', opacity: 0.7 }}>
              API
            </span>
          )}
        </div>
      );
    }
    if (status === 'running') {
      return <span className="text-secondary" style={{ fontSize: '0.85em' }}>Running</span>;
    }
    if (status === 'stopped') {
      return <span className="text-secondary" style={{ fontSize: '0.85em' }}>Stopped</span>;
    }
    if (status === 'provisioning') {
      return <span className="text-secondary" style={{ fontSize: '0.85em' }}>Provisioning...</span>;
    }
    return <span className="text-secondary" style={{ fontSize: '0.85em' }}>Not provisioned</span>;
  }

  // Non-container tenants: show "Enter context" link instead of raw URL
  if (tenant.status === 'active' && onEnterTenant) {
    return (
      <button
        className="tenant-url-link"
        onClick={() => onEnterTenant(tenant)}
        title={`Enter ${tenant.name} tenant context`}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--primary)',
          fontSize: '0.85em',
          padding: 0,
          textDecoration: 'underline',
        }}
      >
        Enter context
      </button>
    );
  }

  return <span className="text-secondary" style={{ fontSize: '0.85em' }}>—</span>;
};

export const TenantTableRow: React.FC<TenantTableRowProps> = ({
  tenant,
  containerStatuses,
  containerTransitioning,
  provisioningStatus,
  containerProvisioningProgress,
  isPurging,
  upgradeInfo,
  onViewDetails,
  onEdit,
  onSuspend,
  onRestore,
  onPurge,
  onArchive,
  onHealthClick,
  onRetryProvisioning,
  onRetryContainerProvisioning,
  onResetProvisioning,
  onUpgrade,
  onViewUpgradeHistory,
  onContainerAction,
  onMigrate,
  onEnterTenant,
}) => {
  const upgrade = upgradeInfo?.[tenant.id];

  return (
  <tr>
    <td>
      <div className="flex items-center gap-3">
        <div
          className="avatar"
          style={{
            background: tenant.branding?.primaryColor || '#7c3aed',
            color: '#fff'
          }}
        >
          {tenant.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <div
            className="font-medium clickable"
            onClick={() => onViewDetails(tenant)}
            style={{ cursor: 'pointer' }}
          >
            {tenant.name}
          </div>
          <div className="text-secondary text-sm">{tenant.slug}</div>
        </div>
      </div>
    </td>
    <td style={{ padding: '8px 4px', textAlign: 'center' }}>
      {tenant.isolation_level === 'container' && upgrade ? (
        (() => {
          // Calculate versions behind
          const availableVersions = upgrade.available_versions || [];
          const currentIdx = availableVersions.indexOf(upgrade.current_version);
          const latestIdx = availableVersions.indexOf(upgrade.latest_version);
          const versionsBehind = currentIdx >= 0 && latestIdx >= 0 ? latestIdx - currentIdx : 0;

          // Determine color: green=current, yellow=1 behind, red=2+ behind
          let color = 'var(--success)'; // green - current
          let title = `Up to date`;
          if (versionsBehind === 1) {
            color = 'var(--warning)'; // yellow - 1 behind
            title = `1 version behind (latest: ${upgrade.latest_version})`;
          } else if (versionsBehind > 1) {
            color = 'var(--error)'; // red - 2+ behind
            title = `${versionsBehind} versions behind (latest: ${upgrade.latest_version})`;
          }

          return upgrade.has_upgrade ? (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onUpgrade?.(tenant)}
              title={title}
              style={{
                padding: '2px 6px',
                minWidth: 'auto',
                color,
                fontWeight: 500,
                fontSize: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <ArrowUpIcon size={12} />
              {upgrade.current_version}
            </button>
          ) : (
            <span
              title={title}
              style={{
                color,
                fontWeight: 500,
                fontSize: '12px',
              }}
            >
              {upgrade.current_version}
            </span>
          );
        })()
      ) : tenant.platform_version ? (
        <span
          title="Up to date"
          style={{
            color: 'var(--success)',
            fontWeight: 500,
            fontSize: '12px',
          }}
        >
          {tenant.platform_version}
        </span>
      ) : (
        <span style={{ color: 'var(--text-secondary)' }}>—</span>
      )}
    </td>
    <td>{renderProvisioningStatus(tenant, provisioningStatus, containerProvisioningProgress, containerStatuses, onRetryProvisioning, onRetryContainerProvisioning, onResetProvisioning)}</td>
    <td>{renderHealthCell(tenant, containerStatuses, containerTransitioning, onHealthClick, containerProvisioningProgress)}</td>
    <td>{getIsolationBadge(tenant.isolation_level)}</td>
    <td>{renderUrlCell(tenant, containerStatuses, onEnterTenant)}</td>
    <td className="text-secondary text-sm">
      {new Date(tenant.created_at).toLocaleDateString()}
    </td>
    <td style={{ overflow: 'visible' }}>
      <div
        className="action-buttons"
        style={{
          display: 'flex',
          gap: '2px',
          alignItems: 'center',
          flexWrap: 'nowrap',
          justifyContent: 'flex-start',
        }}
      >
        <button className="btn btn-ghost btn-sm" onClick={() => onViewDetails(tenant)} title="View details" style={{ padding: '4px 6px', minWidth: 'auto' }}>
          <EyeIcon size={14} />
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => onEdit(tenant)} title="Edit tenant" style={{ padding: '4px 6px', minWidth: 'auto' }}>
          <EditIcon size={14} />
        </button>

        {tenant.status === 'active' && onEnterTenant && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => onEnterTenant(tenant)}
            title="Enter tenant context"
            style={{ padding: '4px 6px', minWidth: 'auto', color: 'var(--primary)' }}
          >
            <BuildingIcon size={14} />
          </button>
        )}

        {tenant.isolation_level === 'container' && containerTransitioning[tenant.id] ? (
          <span style={{ display: 'flex', alignItems: 'center', padding: '0 2px' }}>
            <RefreshIcon size={14} className="spin" style={{ color: 'var(--primary)' }} />
          </span>
        ) : tenant.isolation_level === 'container' && containerStatuses[tenant.id]?.status === 'running' && onContainerAction ? (
          <button className="btn btn-ghost btn-sm" onClick={() => onContainerAction(tenant.id, 'restart')} title="Restart containers" style={{ padding: '4px 6px', minWidth: 'auto' }}>
            <RefreshIcon size={14} />
          </button>
        ) : null}

        {tenant.isolation_level === 'container' && onViewUpgradeHistory && (
          <button className="btn btn-ghost btn-sm" onClick={() => onViewUpgradeHistory(tenant)} title="Upgrade history" style={{ padding: '4px 6px', minWidth: 'auto' }}>
            <HistoryIcon size={14} />
          </button>
        )}

        {tenant.isolation_level !== 'container' && tenant.status === 'active' && onMigrate && (
          <button className="btn btn-ghost btn-sm" onClick={() => onMigrate(tenant)} title="Migrate isolation level" style={{ padding: '4px 6px', minWidth: 'auto', color: 'var(--info)' }}>
            <LayersIcon size={14} />
          </button>
        )}

        {tenant.status === 'suspended' && (
          <button className="btn btn-ghost btn-sm text-success" onClick={() => onSuspend(tenant)} title="Reactivate tenant" style={{ padding: '4px 6px', minWidth: 'auto' }}>
            <CheckCircleIcon size={14} />
          </button>
        )}
        {tenant.status === 'active' && (
          <button className="btn btn-ghost btn-sm" onClick={() => onSuspend(tenant)} title="Suspend tenant" style={{ padding: '4px 6px', minWidth: 'auto' }}>
            <AlertIcon size={14} />
          </button>
        )}

        {tenant.status === 'archived' ? (
          <>
            <button className="btn btn-ghost btn-sm text-success" onClick={() => onRestore(tenant)} title="Restore tenant" style={{ padding: '4px 6px', minWidth: 'auto' }}>
              <UndoIcon size={14} />
            </button>
            <button className="btn btn-ghost btn-sm text-error" onClick={() => onPurge(tenant)} disabled={isPurging === tenant.id} title="Permanently delete" style={{ padding: '4px 6px', minWidth: 'auto' }}>
              {isPurging === tenant.id ? <RefreshIcon size={14} className="spin" /> : <TrashIcon size={14} />}
            </button>
          </>
        ) : (
          <button className="btn btn-ghost btn-sm text-warning" onClick={() => onArchive(tenant)} title="Archive tenant" style={{ padding: '4px 6px', minWidth: 'auto' }}>
            <ArchiveIcon size={14} />
          </button>
        )}
      </div>
    </td>
  </tr>
  );
};
